import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  ArrowUpTrayIcon,
  FolderArrowDownIcon,
  TrashIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline'
import {
  SkillApi,
  SkillPack,
  SkillCatalogItem,
  SkillTreeNode,
  SkillDownloadEvent,
  SkillDownloadEventType,
  SkillPackContentType,
  catalogTypeLabel,
  formatGroupTitle,
  formatSkillDate,
  groupCatalogKey,
  packContentTypeLabel,
} from '../../services/skillApi'
import SkillTree from '../../components/skills/SkillTree'
import SkillFileViewer from '../../components/skills/SkillFileViewer'

const EMPTY_CATALOG: SkillCatalogItem[] = []

const downloadTypeLabel = (t: SkillDownloadEventType) =>
  t === 'PACK' ? 'Cả bộ' : t === 'FOLDER' ? 'Thư mục (ZIP)' : 'File'

const CONTENT_TYPE_OPTIONS: { value: SkillPackContentType; label: string; hint: string }[] = [
  {
    value: 'SKILL',
    label: 'Skill',
    hint: 'ZIP/RAR được giải nén, hệ thống tự nhận diện skill / workflow / artifact.',
  },
  {
    value: 'DOCUMENT',
    label: 'Tài liệu',
    hint: 'PDF, Word, Excel, PowerPoint... Giữ nguyên file, mỗi file là một mục trên thư viện.',
  },
  {
    value: 'INSTALLER',
    label: 'File cài đặt',
    hint: 'ZIP, RAR, EXE, MSI... Giữ nguyên file (không giải nén), mỗi file là một mục trên thư viện.',
  },
]

const AdminSkillLibraryPage = () => {
  const [viewMode, setViewMode] = useState<'packs' | 'downloads'>('packs')
  const [packs, setPacks] = useState<SkillPack[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPackId, setSelectedPackId] = useState<number | null>(null)
  const [tree, setTree] = useState<SkillTreeNode[]>([])
  const [selectedFile, setSelectedFile] = useState<SkillTreeNode | null>(null)
  const [selectedFolder, setSelectedFolder] = useState<SkillTreeNode | null>(null)
  const [uploading, setUploading] = useState(false)
  const [catalogSearch, setCatalogSearch] = useState('')
  const [descDrafts, setDescDrafts] = useState<Record<number, string>>({})
  const [savingDescIds, setSavingDescIds] = useState<Set<number>>(new Set())

  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [uploadMode, setUploadMode] = useState<'zip' | 'folder'>('zip')
  const [contentType, setContentType] = useState<SkillPackContentType>('SKILL')

  const [downloadEvents, setDownloadEvents] = useState<SkillDownloadEvent[]>([])
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [downloadPage, setDownloadPage] = useState(1)
  const [downloadTotalPages, setDownloadTotalPages] = useState(1)
  const [downloadTotal, setDownloadTotal] = useState(0)
  const [downloadEmail, setDownloadEmail] = useState('')
  const [downloadPackId, setDownloadPackId] = useState<number | ''>('')
  const [downloadType, setDownloadType] = useState<SkillDownloadEventType | ''>('')
  const [downloadFrom, setDownloadFrom] = useState('')
  const [downloadTo, setDownloadTo] = useState('')

  const zipRef = useRef<HTMLInputElement>(null)
  const folderRef = useRef<HTMLInputElement>(null)
  const replaceZipRef = useRef<HTMLInputElement>(null)
  const replaceFolderRef = useRef<HTMLInputElement>(null)
  const intoFolderZipRef = useRef<HTMLInputElement>(null)
  const intoFolderDirRef = useRef<HTMLInputElement>(null)
  const intoFolderFilesRef = useRef<HTMLInputElement>(null)
  const addFilesRef = useRef<HTMLInputElement>(null)
  const uploadTargetFolderRef = useRef<SkillTreeNode | null>(null)

  const selectedPack = packs.find((p) => p.id === selectedPackId) || null
  /** Pack Tài liệu / File cài đặt: giữ nguyên file, không giải nén. */
  const isFilePack = !!selectedPack && (selectedPack.contentType ?? 'SKILL') !== 'SKILL'
  const catalogItems: SkillCatalogItem[] = selectedPack?.catalogItems ?? EMPTY_CATALOG

  const filteredCatalogItems = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase()
    if (!q) return catalogItems
    return catalogItems.filter((c) => {
      const hay = [c.name, c.description, c.slug, c.groupPath, c.itemType, catalogTypeLabel(c.itemType)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return hay.includes(q)
    })
  }, [catalogItems, catalogSearch])

  const loadPacks = async () => {
    try {
      setLoading(true)
      const data = await SkillApi.adminListPacks()
      setPacks(data)
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được danh sách skill pack')
    } finally {
      setLoading(false)
    }
  }

  const loadTree = async (packId: number) => {
    try {
      const data = await SkillApi.adminGetTree(packId)
      setTree(data.tree || [])
      setSelectedFile(null)
      setSelectedFolder(null)
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được cây thư mục')
    }
  }

  useEffect(() => {
    loadPacks()
  }, [])

  const loadDownloadEvents = async (page = downloadPage) => {
    try {
      setDownloadLoading(true)
      const data = await SkillApi.adminListDownloadEvents({
        page,
        limit: 20,
        email: downloadEmail,
        packId: downloadPackId === '' ? undefined : Number(downloadPackId),
        downloadType,
        from: downloadFrom || undefined,
        to: downloadTo || undefined,
      })
      setDownloadEvents(data.items || [])
      setDownloadPage(data.pagination.page)
      setDownloadTotalPages(data.pagination.totalPages)
      setDownloadTotal(data.pagination.total)
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được lịch sử tải skill')
    } finally {
      setDownloadLoading(false)
    }
  }

  useEffect(() => {
    if (viewMode === 'downloads') {
      loadDownloadEvents(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode])

  useEffect(() => {
    if (selectedPackId) {
      loadTree(selectedPackId)
      setCatalogSearch('')
      setDescDrafts({})
    } else {
      setTree([])
      setSelectedFile(null)
      setSelectedFolder(null)
      setCatalogSearch('')
      setDescDrafts({})
    }
  }, [selectedPackId])

  useEffect(() => {
    // Sync drafts from server when catalog reloads (keep only dirty fields if still editing)
    setDescDrafts((prev) => {
      const next: Record<number, string> = {}
      for (const c of catalogItems) {
        if (prev[c.id] !== undefined && prev[c.id] !== (c.description || '')) {
          next[c.id] = prev[c.id]
        }
      }
      return next
    })
  }, [catalogItems])

  const nameFromZipFile = (filename: string) =>
    filename.replace(/\.(zip|rar)$/i, '').trim() || filename

  const nameFromFolderFiles = (files: FileList) => {
    const first = files[0] as File & { webkitRelativePath?: string }
    const rel = (first.webkitRelativePath || first.name).replace(/\\/g, '/')
    const root = rel.split('/').filter(Boolean)[0]
    return (root || first.name).trim()
  }

  const buildFolderFormData = (
    files: FileList,
    base?: { name: string; description?: string; contentType?: SkillPackContentType }
  ) => {
    const fd = new FormData()
    if (base) {
      fd.append('name', base.name)
      if (base.description) fd.append('description', base.description)
      if (base.contentType) fd.append('contentType', base.contentType)
    }
    const paths: string[] = []
    Array.from(files).forEach((file) => {
      const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name
      fd.append('files', file)
      paths.push(rel)
    })
    fd.append('relativePaths', JSON.stringify(paths))
    return fd
  }

  const onZipSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (contentType !== 'SKILL') {
      // Tên bộ do admin đặt; gợi ý từ file đầu tiên nếu đang trống.
      if (!name.trim() && file) setName(file.name.replace(/\.[a-z0-9]+$/i, ''))
      return
    }
    setName(file ? nameFromZipFile(file.name) : '')
  }

  const onFolderSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (contentType !== 'SKILL' && name.trim()) return
    setName(files && files.length > 0 ? nameFromFolderFiles(files) : '')
  }

  const handleCreateUpload = async () => {
    try {
      setUploading(true)
      if (contentType !== 'SKILL') {
        const files =
          uploadMode === 'zip' ? zipRef.current?.files : folderRef.current?.files
        if (!files || files.length === 0) {
          toast.error(uploadMode === 'zip' ? 'Vui lòng chọn file' : 'Vui lòng chọn thư mục')
          return
        }
        const packName = name.trim() || nameFromFolderFiles(files)
        const fd = buildFolderFormData(files, {
          name: packName,
          description: description.trim() || undefined,
          contentType,
        })
        await SkillApi.uploadNew(fd)
        toast.success(`Đã tạo bộ ${packContentTypeLabel(contentType).toLowerCase()}`)
        setShowCreate(false)
        setName('')
        setDescription('')
        if (zipRef.current) zipRef.current.value = ''
        if (folderRef.current) folderRef.current.value = ''
        await loadPacks()
        return
      }
      if (uploadMode === 'zip') {
        const file = zipRef.current?.files?.[0]
        if (!file) {
          toast.error('Vui lòng chọn file ZIP hoặc RAR')
          return
        }
        const skillName = nameFromZipFile(file.name)
        const fd = new FormData()
        fd.append('name', skillName)
        if (description.trim()) fd.append('description', description.trim())
        fd.append('zip', file)
        await SkillApi.uploadNew(fd)
      } else {
        const files = folderRef.current?.files
        if (!files || files.length === 0) {
          toast.error('Vui lòng chọn thư mục')
          return
        }
        const skillName = nameFromFolderFiles(files)
        const fd = buildFolderFormData(files, {
          name: skillName,
          description: description.trim() || undefined,
        })
        await SkillApi.uploadNew(fd)
      }
      toast.success('Đã tạo bộ skill')
      setShowCreate(false)
      setName('')
      setDescription('')
      if (zipRef.current) zipRef.current.value = ''
      if (folderRef.current) folderRef.current.value = ''
      await loadPacks()
    } catch (e: any) {
      toast.error(e?.message || 'Upload thất bại')
    } finally {
      setUploading(false)
    }
  }

  const handleReplaceZip = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selectedPackId) return
    try {
      setUploading(true)
      const fd = new FormData()
      fd.append('zip', file)
      const result = await SkillApi.uploadReplace(selectedPackId, fd)
      toast.success(`Đã cập nhật đè ${result.fileCount} file`)
      await loadPacks()
      await loadTree(selectedPackId)
    } catch (err: any) {
      toast.error(err?.message || 'Cập nhật thất bại')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const handleReplaceFolder = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0 || !selectedPackId) return
    try {
      setUploading(true)
      const fd = buildFolderFormData(files)
      const result = await SkillApi.uploadReplace(selectedPackId, fd)
      toast.success(
        isFilePack ? `Đã thêm ${result.fileCount} file` : `Đã cập nhật đè ${result.fileCount} file`
      )
      await loadPacks()
      await loadTree(selectedPackId)
    } catch (err: any) {
      toast.error(err?.message || 'Cập nhật thất bại')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const openUploadIntoFolder = (folder: SkillTreeNode, mode: 'zip' | 'folder') => {
    uploadTargetFolderRef.current = folder
    setSelectedFolder(folder)
    setSelectedFile(null)
    if (mode === 'zip') {
      // Pack giữ nguyên file: chọn nhiều file bất kỳ, không giải nén.
      if (isFilePack) intoFolderFilesRef.current?.click()
      else intoFolderZipRef.current?.click()
    } else intoFolderDirRef.current?.click()
  }

  const handleUploadIntoFolderZip = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const target = uploadTargetFolderRef.current || selectedFolder
    if (!file || !target || !selectedPackId) return
    try {
      setUploading(true)
      const fd = new FormData()
      fd.append('zip', file)
      const result = await SkillApi.uploadIntoFolder(target.id, fd)
      toast.success(`Đã thêm ${result.fileCount} file vào "${target.name}"`)
      await loadPacks()
      await loadTree(selectedPackId)
      setSelectedFolder(target)
    } catch (err: any) {
      toast.error(err?.message || 'Upload vào thư mục thất bại')
    } finally {
      setUploading(false)
      e.target.value = ''
      uploadTargetFolderRef.current = null
    }
  }

  const handleUploadIntoFolderDir = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    const target = uploadTargetFolderRef.current || selectedFolder
    if (!files || files.length === 0 || !target || !selectedPackId) return
    try {
      setUploading(true)
      const fd = buildFolderFormData(files)
      const result = await SkillApi.uploadIntoFolder(target.id, fd)
      toast.success(`Đã thêm ${result.fileCount} file vào "${target.name}"`)
      await loadPacks()
      await loadTree(selectedPackId)
      setSelectedFolder(target)
    } catch (err: any) {
      toast.error(err?.message || 'Upload vào thư mục thất bại')
    } finally {
      setUploading(false)
      e.target.value = ''
      uploadTargetFolderRef.current = null
    }
  }

  /** Pack Tài liệu / File cài đặt: thêm file vào gốc (trùng tên thì ghi đè). */
  const handleAddFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0 || !selectedPackId) return
    try {
      setUploading(true)
      const fd = buildFolderFormData(files)
      const result = await SkillApi.uploadReplace(selectedPackId, fd)
      toast.success(`Đã thêm ${result.fileCount} file`)
      await loadPacks()
      await loadTree(selectedPackId)
    } catch (err: any) {
      toast.error(err?.message || 'Thêm file thất bại')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const handleDeleteNode = async (node: SkillTreeNode) => {
    if (!selectedPackId) return
    const what = node.nodeType === 'FOLDER' ? `thư mục "${node.name}" và mọi file bên trong` : `file "${node.name}"`
    if (!window.confirm(`Xóa ${what}? Hành động không hoàn tác.`)) return
    try {
      setUploading(true)
      await SkillApi.deleteNode(node.id)
      toast.success(`Đã xóa ${node.name}`)
      await loadPacks()
      await loadTree(selectedPackId)
    } catch (err: any) {
      toast.error(err?.message || 'Xóa thất bại')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (pack: SkillPack) => {
    if (!window.confirm(`Xóa bộ "${pack.name}"? Hành động không hoàn tác.`)) return
    try {
      await SkillApi.deletePack(pack.id)
      toast.success('Đã xóa')
      if (selectedPackId === pack.id) setSelectedPackId(null)
      await loadPacks()
    } catch (e: any) {
      toast.error(e?.message || 'Xóa thất bại')
    }
  }

  const handleTogglePublic = async (item: SkillCatalogItem) => {
    try {
      await SkillApi.updateCatalogItem(item.id, { isPublic: !item.isPublic })
      toast.success(item.isPublic ? 'Đã chuyển Private' : 'Đã chuyển Public')
      await loadPacks()
    } catch (e: any) {
      toast.error(e?.message || 'Cập nhật thất bại')
    }
  }

  const handleToggleHidden = async (item: SkillCatalogItem) => {
    try {
      await SkillApi.updateCatalogItem(item.id, { isHidden: !item.isHidden })
      toast.success(item.isHidden ? 'Đã hiện trên thư viện' : 'Đã ẩn khỏi thư viện')
      await loadPacks()
    } catch (e: any) {
      toast.error(e?.message || 'Cập nhật thất bại')
    }
  }

  const handleToggleGroupPublic = async (groupItems: SkillCatalogItem[], makePublic: boolean) => {
    try {
      setUploading(true)
      await Promise.all(
        groupItems.map((c) => SkillApi.updateCatalogItem(c.id, { isPublic: makePublic }))
      )
      toast.success(makePublic ? 'Đã Public cả nhóm' : 'Đã Private cả nhóm')
      await loadPacks()
    } catch (e: any) {
      toast.error(e?.message || 'Cập nhật nhóm thất bại')
    } finally {
      setUploading(false)
    }
  }

  const handleToggleGroupHidden = async (groupItems: SkillCatalogItem[], hide: boolean) => {
    try {
      setUploading(true)
      await Promise.all(
        groupItems.map((c) => SkillApi.updateCatalogItem(c.id, { isHidden: hide }))
      )
      toast.success(hide ? 'Đã ẩn cả nhóm khỏi thư viện' : 'Đã hiện cả nhóm trên thư viện')
      await loadPacks()
    } catch (e: any) {
      toast.error(e?.message || 'Cập nhật nhóm thất bại')
    } finally {
      setUploading(false)
    }
  }

  const handleSaveCatalogDesc = async (item: SkillCatalogItem) => {
    const description = (descDrafts[item.id] ?? item.description ?? '').trim()
    if (description === (item.description || '').trim()) {
      toast.info('Mô tả chưa thay đổi')
      return
    }
    try {
      setSavingDescIds((prev) => new Set(prev).add(item.id))
      await SkillApi.updateCatalogItem(item.id, { description })
      setDescDrafts((prev) => {
        const next = { ...prev }
        delete next[item.id]
        return next
      })
      toast.success('Đã lưu mô tả')
      await loadPacks()
    } catch (e: any) {
      toast.error(e?.message || 'Lưu mô tả thất bại')
    } finally {
      setSavingDescIds((prev) => {
        const next = new Set(prev)
        next.delete(item.id)
        return next
      })
    }
  }

  const descValue = (item: SkillCatalogItem) =>
    descDrafts[item.id] !== undefined ? descDrafts[item.id] : item.description || ''

  const descDirty = (item: SkillCatalogItem) =>
    (descDrafts[item.id] ?? item.description ?? '').trim() !== (item.description || '').trim()

  const handleReclassify = async () => {
    if (!selectedPackId) return
    try {
      setUploading(true)
      await SkillApi.reclassifyPack(selectedPackId)
      toast.success('Đã nhận diện lại catalog')
      await loadPacks()
    } catch (e: any) {
      toast.error(e?.message || 'Reclassify thất bại')
    } finally {
      setUploading(false)
    }
  }

  const selectedNodeId = selectedFile?.id ?? selectedFolder?.id ?? null

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-m2-surface-container p-6 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-m2-on-surface">Thư viện (Skill · Tài liệu · File cài đặt)</h1>
          <p className="mt-1 text-sm text-m2-on-surface-variant">
            Đẩy bộ skill (ZIP/RAR/thư mục, tự giải nén), tài liệu hoặc file cài đặt (giữ nguyên file).
            Mọi lượt tải đều yêu cầu đăng nhập và được ghi vào Lịch sử tải.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setViewMode('packs')}
              className={`rounded-md px-3 py-1.5 text-sm ${
                viewMode === 'packs'
                  ? 'bg-m2-primary text-m2-on-primary'
                  : 'border border-m2-outline text-m2-on-surface hover:bg-m2-surface-high'
              }`}
            >
              Quản lý pack
            </button>
            <button
              type="button"
              onClick={() => setViewMode('downloads')}
              className={`rounded-md px-3 py-1.5 text-sm ${
                viewMode === 'downloads'
                  ? 'bg-m2-primary text-m2-on-primary'
                  : 'border border-m2-outline text-m2-on-surface hover:bg-m2-surface-high'
              }`}
            >
              Lịch sử tải
            </button>
          </div>
        </div>
        {viewMode === 'packs' && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 rounded-md bg-m2-primary px-4 py-2 text-m2-on-primary hover:bg-eh-forest-shadow"
          >
            <ArrowUpTrayIcon className="h-5 w-5" />
            Thêm mới
          </button>
        )}
      </div>

      {viewMode === 'downloads' ? (
        <div className="space-y-4 rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="mb-1 block text-m2-on-surface-variant">Email</span>
              <input
                type="text"
                value={downloadEmail}
                onChange={(e) => setDownloadEmail(e.target.value)}
                className="rounded-md border border-m2-outline bg-m2-surface px-3 py-1.5 text-sm"
                placeholder="lọc theo email"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-m2-on-surface-variant">Bộ</span>
              <select
                value={downloadPackId}
                onChange={(e) =>
                  setDownloadPackId(e.target.value ? Number(e.target.value) : '')
                }
                className="rounded-md border border-m2-outline bg-m2-surface px-3 py-1.5 text-sm"
              >
                <option value="">Tất cả</option>
                {packs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-m2-on-surface-variant">Loại</span>
              <select
                value={downloadType}
                onChange={(e) =>
                  setDownloadType((e.target.value as SkillDownloadEventType | '') || '')
                }
                className="rounded-md border border-m2-outline bg-m2-surface px-3 py-1.5 text-sm"
              >
                <option value="">Tất cả</option>
                <option value="PACK">Cả bộ</option>
                <option value="FOLDER">Thư mục (ZIP)</option>
                <option value="FILE">File</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-m2-on-surface-variant">Từ ngày</span>
              <input
                type="date"
                value={downloadFrom}
                onChange={(e) => setDownloadFrom(e.target.value)}
                className="rounded-md border border-m2-outline bg-m2-surface px-3 py-1.5 text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-m2-on-surface-variant">Đến ngày</span>
              <input
                type="date"
                value={downloadTo}
                onChange={(e) => setDownloadTo(e.target.value)}
                className="rounded-md border border-m2-outline bg-m2-surface px-3 py-1.5 text-sm"
              />
            </label>
            <button
              type="button"
              onClick={() => loadDownloadEvents(1)}
              className="rounded-md bg-m2-primary px-4 py-1.5 text-sm text-m2-on-primary"
            >
              Lọc
            </button>
          </div>

          <p className="text-xs text-m2-on-surface-variant">{downloadTotal} lượt tải</p>

          {downloadLoading ? (
            <p className="text-sm text-m2-on-surface-variant">Đang tải...</p>
          ) : downloadEvents.length === 0 ? (
            <p className="text-sm text-m2-on-surface-variant">Chưa có lịch sử tải</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-m2-outline-variant text-xs uppercase text-m2-on-surface-variant">
                  <tr>
                    <th className="px-3 py-2">Thời gian</th>
                    <th className="px-3 py-2">Người tải</th>
                    <th className="px-3 py-2">Nội dung tải</th>
                    <th className="px-3 py-2">Loại</th>
                    <th className="px-3 py-2">Bộ</th>
                    <th className="px-3 py-2">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-m2-outline-variant">
                  {downloadEvents.map((ev) => (
                    <tr key={ev.id} className="align-top">
                      <td className="whitespace-nowrap px-3 py-2 text-m2-on-surface-variant">
                        {formatSkillDate(ev.createdAt)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-m2-on-surface">
                          {ev.userFullName || '—'}
                        </div>
                        <div className="text-xs text-m2-on-surface-variant">{ev.userEmail}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-m2-on-surface">{ev.resourceName}</div>
                        {ev.catalogItemName && (
                          <div className="text-xs text-m2-on-surface-variant">
                            {ev.catalogItemName}
                          </div>
                        )}
                        {ev.relativePath && (
                          <div className="text-xs text-m2-on-surface-variant">{ev.relativePath}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">{downloadTypeLabel(ev.downloadType)}</td>
                      <td className="px-3 py-2 text-m2-on-surface">{ev.packName}</td>
                      <td className="px-3 py-2 text-xs text-m2-on-surface-variant">
                        {ev.ip || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {downloadTotalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={downloadPage <= 1 || downloadLoading}
                onClick={() => loadDownloadEvents(downloadPage - 1)}
                className="rounded-md border border-m2-outline px-3 py-1 text-sm disabled:opacity-50"
              >
                Trước
              </button>
              <span className="text-sm text-m2-on-surface-variant">
                Trang {downloadPage}/{downloadTotalPages}
              </span>
              <button
                type="button"
                disabled={downloadPage >= downloadTotalPages || downloadLoading}
                onClick={() => loadDownloadEvents(downloadPage + 1)}
                className="rounded-md border border-m2-outline px-3 py-1 text-sm disabled:opacity-50"
              >
                Sau
              </button>
            </div>
          )}
        </div>
      ) : (
      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container">
          <div className="border-b border-m2-outline-variant px-4 py-3 text-sm font-semibold text-m2-on-surface">
            Danh sách bộ
          </div>
          {loading ? (
            <p className="p-4 text-sm text-m2-on-surface-variant">Đang tải...</p>
          ) : packs.length === 0 ? (
            <p className="p-4 text-sm text-m2-on-surface-variant">Chưa có bộ nào</p>
          ) : (
            <ul className="max-h-[70vh] divide-y divide-m2-outline-variant overflow-y-auto">
              {packs.map((pack) => (
                <li key={pack.id}>
                  <button
                    type="button"
                    className={`w-full px-4 py-3 text-left hover:bg-m2-surface-high ${
                      selectedPackId === pack.id ? 'bg-m2-primary/10' : ''
                    }`}
                    onClick={() => setSelectedPackId(pack.id)}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="rounded-full bg-m2-surface-high px-2 py-0.5 text-[10px] text-m2-on-surface-variant">
                        {packContentTypeLabel(pack.contentType)}
                      </span>
                      <span className="font-medium text-m2-on-surface">{pack.name}</span>
                    </div>
                    <div className="mt-1 text-xs text-m2-on-surface-variant">
                      Đẩy: {formatSkillDate(pack.createdAt)}
                    </div>
                    <div className="text-xs text-m2-on-surface-variant">
                      Cập nhật: {formatSkillDate(pack.updatedAt)}
                    </div>
                    <div className="mt-1 text-xs text-m2-on-surface-variant">
                      {pack.fileCount ?? 0} file · {pack.folderCount ?? 0} thư mục
                      {(pack.catalogItems?.length ?? 0) > 0 && (
                        <> · {pack.catalogItems!.length} catalog</>
                      )}
                    </div>
                    {(pack.catalogItems || []).some((c) => c.isPublic) && (
                      <span className="mt-1 inline-flex rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-800">
                        Có Public
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-4">
          {!selectedPack ? (
            <div className="rounded-lg border border-dashed border-m2-outline-variant bg-m2-surface-low p-10 text-center text-m2-on-surface-variant">
              Chọn một bộ để xem và cập nhật
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-m2-on-surface">
                      {selectedPack.name}{' '}
                      <span className="align-middle text-xs font-normal text-m2-on-surface-variant">
                        ({packContentTypeLabel(selectedPack.contentType)})
                      </span>
                    </h2>
                    {selectedPack.description && (
                      <p className="mt-1 text-sm text-m2-on-surface-variant">{selectedPack.description}</p>
                    )}
                    <p className="mt-2 text-xs text-m2-on-surface-variant">
                      Slug: {selectedPack.slug} · Đẩy lần đầu: {formatSkillDate(selectedPack.createdAt)} ·
                      Cập nhật mới nhất: {formatSkillDate(selectedPack.updatedAt)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {isFilePack && (
                      <button
                        type="button"
                        disabled={uploading}
                        onClick={() => addFilesRef.current?.click()}
                        className="inline-flex items-center gap-1 rounded-md bg-m2-primary px-3 py-1.5 text-sm text-m2-on-primary disabled:opacity-50"
                      >
                        <ArrowUpTrayIcon className="h-4 w-4" />
                        Thêm file
                      </button>
                    )}
                    {!isFilePack && (
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => replaceZipRef.current?.click()}
                      className="inline-flex items-center gap-1 rounded-md border border-m2-outline px-3 py-1.5 text-sm hover:bg-m2-surface-high disabled:opacity-50"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      Đè bằng ZIP/RAR
                    </button>
                    )}
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => replaceFolderRef.current?.click()}
                      className="inline-flex items-center gap-1 rounded-md border border-m2-outline px-3 py-1.5 text-sm hover:bg-m2-surface-high disabled:opacity-50"
                    >
                      <FolderArrowDownIcon className="h-4 w-4" />
                      {isFilePack ? 'Thêm thư mục' : 'Đè bằng thư mục'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(selectedPack)}
                      className="inline-flex items-center gap-1 rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Xóa
                    </button>
                  </div>
                </div>
                <input
                  ref={replaceZipRef}
                  type="file"
                  accept=".zip,.rar,application/zip,application/x-rar-compressed,application/vnd.rar"
                  className="hidden"
                  onChange={handleReplaceZip}
                />
                <input
                  ref={replaceFolderRef}
                  type="file"
                  className="hidden"
                  // @ts-expect-error webkitdirectory
                  webkitdirectory=""
                  multiple
                  onChange={handleReplaceFolder}
                />
                <input
                  ref={addFilesRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleAddFiles}
                />
                <input
                  ref={intoFolderFilesRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleUploadIntoFolderDir}
                />
                <input
                  ref={intoFolderZipRef}
                  type="file"
                  accept=".zip,.rar,application/zip,application/x-rar-compressed,application/vnd.rar"
                  className="hidden"
                  onChange={handleUploadIntoFolderZip}
                />
                <input
                  ref={intoFolderDirRef}
                  type="file"
                  className="hidden"
                  // @ts-expect-error webkitdirectory
                  webkitdirectory=""
                  multiple
                  onChange={handleUploadIntoFolderDir}
                />
                {uploading && (
                  <p className="mt-2 text-sm text-m2-primary">Đang upload / cập nhật...</p>
                )}
              </div>

              <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-m2-on-surface">
                      {isFilePack
                        ? `Danh mục ${packContentTypeLabel(selectedPack.contentType).toLowerCase()}`
                        : 'Catalog (Skill / Workflow / Artifact)'}
                    </h3>
                    <p className="text-xs text-m2-on-surface-variant">
                      Public = cho tải trên thư viện. Ẩn = không hiện trên trang thư viện (vẫn gắn khóa
                      học được). Có thể ẩn cả thư mục nhóm.
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={handleReclassify}
                    className="rounded-md border border-m2-outline px-3 py-1.5 text-sm hover:bg-m2-surface-high disabled:opacity-50"
                  >
                    Nhận diện lại
                  </button>
                </div>

                {catalogItems.length > 0 && (
                  <div className="relative mb-3">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-m2-on-surface-variant" />
                    <input
                      type="search"
                      value={catalogSearch}
                      onChange={(e) => setCatalogSearch(e.target.value)}
                      placeholder="Tìm skill / workflow / artifact theo tên, mô tả, nhóm..."
                      className="w-full rounded-md border border-m2-outline bg-m2-surface-low py-2 pl-9 pr-3 text-sm text-m2-on-surface placeholder:text-m2-on-surface-variant focus:border-m2-primary focus:outline-none focus:ring-1 focus:ring-m2-primary"
                    />
                  </div>
                )}

                {catalogItems.length === 0 ? (
                  <p className="text-sm text-m2-on-surface-variant">
                    {isFilePack
                      ? 'Chưa có file nào. Bấm "Thêm file" để upload.'
                      : 'Chưa nhận diện được item. Upload bundle có SKILL.md / workflow / hoặc bấm Nhận diện lại.'}
                  </p>
                ) : filteredCatalogItems.length === 0 ? (
                  <p className="text-sm text-m2-on-surface-variant">
                    Không có kết quả cho &quot;{catalogSearch.trim()}&quot;
                  </p>
                ) : (
                  <div className="max-h-[28rem] space-y-4 overflow-y-auto">
                    {(() => {
                      const map = new Map<string, SkillCatalogItem[]>()
                      for (const c of filteredCatalogItems) {
                        const key = groupCatalogKey(c)
                        const list = map.get(key) || []
                        list.push(c)
                        map.set(key, list)
                      }
                      return Array.from(map.entries()).map(([key, groupItems]) => {
                        const sample = groupItems[0]
                        const allPublic = groupItems.every((c) => c.isPublic)
                        const allHidden = groupItems.every((c) => c.isHidden)
                        const title = formatGroupTitle(sample.groupPath, sample.itemType, sample.packName)
                        return (
                          <div key={key} className="rounded-md border border-m2-outline-variant">
                            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-m2-outline-variant bg-m2-surface-high px-3 py-2">
                              <div className="font-medium text-m2-on-surface">
                                {title}{' '}
                                <span className="text-xs font-normal text-m2-on-surface-variant">
                                  ({groupItems.length})
                                  {allHidden ? ' · đang ẩn' : ''}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                <button
                                  type="button"
                                  disabled={uploading}
                                  onClick={() => handleToggleGroupHidden(groupItems, !allHidden)}
                                  className="rounded border border-m2-outline bg-m2-surface-container px-2 py-1 text-xs hover:bg-m2-surface-low disabled:opacity-50"
                                >
                                  {allHidden ? 'Hiện cả nhóm' : 'Ẩn cả nhóm'}
                                </button>
                                <button
                                  type="button"
                                  disabled={uploading}
                                  onClick={() => handleToggleGroupPublic(groupItems, !allPublic)}
                                  className="rounded border border-m2-outline bg-m2-surface-container px-2 py-1 text-xs hover:bg-m2-surface-low disabled:opacity-50"
                                >
                                  {allPublic ? 'Private cả nhóm' : 'Public cả nhóm'}
                                </button>
                              </div>
                            </div>
                            <ul className="space-y-2 p-2">
                              {groupItems.map((c) => (
                                <li
                                  key={c.id}
                                  className={`rounded-md border border-m2-outline-variant p-3 ${
                                    c.isHidden ? 'bg-m2-surface-high/80 opacity-80' : 'bg-m2-surface-low'
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-1.5">
                                        <span className="rounded-full bg-m2-surface-high px-2 py-0.5 text-[10px]">
                                          {catalogTypeLabel(c.itemType)}
                                        </span>
                                        <span
                                          className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                            c.isPublic
                                              ? 'bg-green-100 text-green-800'
                                              : 'bg-m2-surface-high text-m2-on-surface-variant'
                                          }`}
                                        >
                                          {c.isPublic ? 'Public' : 'Private'}
                                        </span>
                                        {c.isHidden && (
                                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                                            Ẩn thư viện
                                          </span>
                                        )}
                                      </div>
                                      <div className="mt-1 font-medium text-m2-on-surface">{c.name}</div>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-1">
                                      <button
                                        type="button"
                                        title={c.isPublic ? 'Public — bấm để Private' : 'Private — bấm để Public'}
                                        onClick={() => handleTogglePublic(c)}
                                        className={`rounded-md p-1.5 transition-colors ${
                                          c.isPublic
                                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                            : 'bg-m2-surface-high text-m2-on-surface-variant hover:bg-m2-surface-container'
                                        }`}
                                      >
                                        {c.isPublic ? (
                                          <GlobeAltIcon className="h-4 w-4" />
                                        ) : (
                                          <LockClosedIcon className="h-4 w-4" />
                                        )}
                                      </button>
                                      <button
                                        type="button"
                                        title={
                                          c.isHidden
                                            ? 'Đang ẩn thư viện — bấm để hiện'
                                            : 'Đang hiện thư viện — bấm để ẩn'
                                        }
                                        onClick={() => handleToggleHidden(c)}
                                        className={`rounded-md p-1.5 transition-colors ${
                                          c.isHidden
                                            ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                            : 'bg-m2-surface-high text-m2-on-surface-variant hover:bg-m2-surface-container'
                                        }`}
                                      >
                                        {c.isHidden ? (
                                          <EyeSlashIcon className="h-4 w-4" />
                                        ) : (
                                          <EyeIcon className="h-4 w-4" />
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="mt-2 flex items-start gap-2">
                                    <textarea
                                      className="min-w-0 flex-1 rounded-md border border-m2-outline bg-m2-surface-container px-2 py-1.5 text-sm"
                                      rows={2}
                                      value={descValue(c)}
                                      placeholder="Mô tả hiển thị trên trang thư viện (có thể sửa lại sau khi hệ thống nhận diện)"
                                      onChange={(e) =>
                                        setDescDrafts((prev) => ({
                                          ...prev,
                                          [c.id]: e.target.value,
                                        }))
                                      }
                                    />
                                    <button
                                      type="button"
                                      disabled={savingDescIds.has(c.id) || !descDirty(c)}
                                      onClick={() => handleSaveCatalogDesc(c)}
                                      className="shrink-0 rounded-md bg-m2-primary px-3 py-1.5 text-sm font-medium text-m2-on-primary hover:bg-eh-forest-shadow disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                      {savingDescIds.has(c.id) ? '...' : 'Save'}
                                    </button>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )
                      })
                    })()}
                  </div>
                )}
              </div>

              {selectedFolder && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-m2-primary/30 bg-m2-primary/5 px-4 py-3">
                  <div className="text-sm text-m2-on-surface">
                    Đang chọn thư mục: <span className="font-semibold">{selectedFolder.name}</span>
                    <span className="ml-2 text-xs text-m2-on-surface-variant">
                      ({selectedFolder.relativePath})
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => openUploadIntoFolder(selectedFolder, 'zip')}
                      className="inline-flex items-center gap-1 rounded-md bg-m2-primary px-3 py-1.5 text-sm text-m2-on-primary disabled:opacity-50"
                    >
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      {isFilePack ? 'Thêm file vào đây' : 'Thêm ZIP/RAR vào đây'}
                    </button>
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => openUploadIntoFolder(selectedFolder, 'folder')}
                      className="inline-flex items-center gap-1 rounded-md border border-m2-outline px-3 py-1.5 text-sm hover:bg-m2-surface-high disabled:opacity-50"
                    >
                      <FolderArrowDownIcon className="h-4 w-4" />
                      Thêm thư mục vào đây
                    </button>
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => handleDeleteNode(selectedFolder)}
                      className="inline-flex items-center gap-1 rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Xóa thư mục
                    </button>
                  </div>
                </div>
              )}

              {selectedFile && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-m2-outline-variant bg-m2-surface-low px-4 py-3">
                  <div className="text-sm text-m2-on-surface">
                    Đang chọn file: <span className="font-semibold">{selectedFile.name}</span>
                    <span className="ml-2 text-xs text-m2-on-surface-variant">
                      ({selectedFile.relativePath})
                    </span>
                  </div>
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={() => handleDeleteNode(selectedFile)}
                    className="inline-flex items-center gap-1 rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Xóa file
                  </button>
                </div>
              )}

              <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
                <div className="max-h-[70vh] overflow-y-auto rounded-lg border border-m2-outline-variant bg-m2-surface-container">
                  <SkillTree
                    nodes={tree}
                    selectedId={selectedNodeId}
                    onSelectFile={(node) => {
                      setSelectedFile(node)
                      setSelectedFolder(null)
                    }}
                    onSelectFolder={(node) => {
                      setSelectedFolder(node)
                      setSelectedFile(null)
                    }}
                    onUploadIntoFolder={(node) => openUploadIntoFolder(node, 'zip')}
                  />
                </div>
                <SkillFileViewer node={selectedFile} />
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-m2-surface-container shadow-xl">
            <div className="border-b border-m2-outline-variant px-5 py-4">
              <h2 className="text-lg font-semibold">Thêm mới vào thư viện</h2>
            </div>
            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1 block text-sm font-medium">Loại nội dung</label>
                <div className="flex flex-wrap gap-3">
                  {CONTENT_TYPE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        checked={contentType === opt.value}
                        onChange={() => {
                          setContentType(opt.value)
                          setName('')
                          if (zipRef.current) zipRef.current.value = ''
                          if (folderRef.current) folderRef.current.value = ''
                        }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-m2-on-surface-variant">
                  {CONTENT_TYPE_OPTIONS.find((o) => o.value === contentType)?.hint}
                </p>
              </div>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={uploadMode === 'zip'}
                    onChange={() => {
                      setUploadMode('zip')
                      setName('')
                      if (zipRef.current) zipRef.current.value = ''
                      if (folderRef.current) folderRef.current.value = ''
                    }}
                  />
                  {contentType === 'SKILL' ? 'Upload ZIP / RAR' : 'Chọn file'}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={uploadMode === 'folder'}
                    onChange={() => {
                      setUploadMode('folder')
                      setName('')
                      if (zipRef.current) zipRef.current.value = ''
                      if (folderRef.current) folderRef.current.value = ''
                    }}
                  />
                  Upload thư mục
                </label>
              </div>
              {uploadMode === 'zip' ? (
                contentType === 'SKILL' ? (
                  <input
                    ref={zipRef}
                    type="file"
                    accept=".zip,.rar,application/zip,application/x-rar-compressed,application/vnd.rar"
                    className="w-full text-sm"
                    onChange={onZipSelected}
                  />
                ) : (
                  <input
                    key="raw-files"
                    ref={zipRef}
                    type="file"
                    multiple
                    className="w-full text-sm"
                    onChange={onZipSelected}
                  />
                )
              ) : (
                <input
                  ref={folderRef}
                  type="file"
                  className="w-full text-sm"
                  // @ts-expect-error webkitdirectory
                  webkitdirectory=""
                  multiple
                  onChange={onFolderSelected}
                />
              )}
              <div>
                <label className="mb-1 block text-sm font-medium">Tên bộ</label>
                <input
                  className="w-full rounded-md border border-m2-outline bg-m2-surface-low px-3 py-2 text-m2-on-surface"
                  value={name}
                  readOnly={contentType === 'SKILL'}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={
                    contentType !== 'SKILL'
                      ? 'Ví dụ: Bộ cài đặt công cụ AI'
                      : uploadMode === 'zip'
                        ? 'Tự lấy từ tên file ZIP/RAR (bỏ đuôi)'
                        : 'Tự lấy từ tên thư mục gốc'
                  }
                />
                <p className="mt-1 text-xs text-m2-on-surface-variant">
                  {contentType !== 'SKILL'
                    ? 'Tên nhóm hiển thị trên thư viện. Mỗi file bên trong là một mục tải về riêng.'
                    : `Trùng với tên ${uploadMode === 'zip' ? 'file ZIP/RAR' : 'thư mục'} bạn chọn upload.`}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Mô tả pack (tùy chọn)</label>
                <textarea
                  className="w-full rounded-md border border-m2-outline px-3 py-2"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <p className="mt-1 text-xs text-m2-on-surface-variant">
                  {contentType === 'SKILL'
                    ? 'Sau upload, hệ thống tự nhận diện từng skill (SKILL.md). Mô tả từng skill chỉnh trong catalog — hiện trên trang thư viện công khai.'
                    : 'Mục mới mặc định Private. Bật Public trong danh mục để mọi người (đã đăng nhập) tải được.'}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-m2-outline-variant px-5 py-4">
              <button
                type="button"
                className="rounded-md border border-m2-outline px-4 py-2 text-sm"
                onClick={() => setShowCreate(false)}
                disabled={uploading}
              >
                Hủy
              </button>
              <button
                type="button"
                className="rounded-md bg-m2-primary px-4 py-2 text-sm text-m2-on-primary disabled:opacity-50"
                onClick={handleCreateUpload}
                disabled={uploading}
              >
                {uploading ? 'Đang upload...' : 'Tạo & Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminSkillLibraryPage
